import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../hooks/storeHook';
import { fetchSuggestions } from '../store/action';
import config from '../config';

interface TypeaheadProps {
  inputText: string;
  debouncedInputText?: string; // Add debounced input prop
  onSelect: (text: string) => void;
  onClose: () => void;
  visible: boolean;
  customerId?: string | null;
  mspId?: string | null;
  ticketId?: string | null;
  onNavigationChange?: (hasNavigated: boolean) => void; // Add this prop
}

const Typeahead: React.FC<TypeaheadProps> = ({
  inputText,
  debouncedInputText,
  onSelect,
  onClose,
  visible,
  customerId,
  mspId,
  ticketId,
  onNavigationChange
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1); // Start with no selection
  const [hasNavigated, setHasNavigated] = useState(false); // Track if user has navigated to suggestions
  const dispatch = useAppDispatch();
  const suggestions = useSelector((state: any) => state.suggestions.suggestions);
  const isLoading = useSelector((state: any) => state.suggestions.isLoading);
  
  // Track the last input we made an API call for to prevent duplicates
  const lastApiCallRef = useRef<string>('');

  // Reset selected index when input changes
  useEffect(() => {
    setSelectedIndex(-1); // Reset to no selection
    setHasNavigated(false); // Reset navigation state when input changes
  }, [inputText]);

  // Reset API call tracking when typeahead becomes invisible
  useEffect(() => {
    if (!visible) {
      lastApiCallRef.current = '';
      setHasNavigated(false); // Reset navigation state when typeahead closes
    }
  }, [visible]);

  // Notify parent when navigation state changes
  useEffect(() => {
    if (onNavigationChange) {
      onNavigationChange(hasNavigated);
    }
  }, [hasNavigated, onNavigationChange]);

  // Fetch suggestions with debouncing and minimum character threshold
  useEffect(() => {
    // Skip API calls when typeahead suggestions are disabled (e.g. prod)
    if (!config.typeaheadSuggestionsEnabled) {
      return;
    }

    // Use debouncedInputText if available, otherwise fall back to inputText
    const textToSearch = debouncedInputText || inputText;

    if (!visible || !textToSearch.trim() || (!customerId && !mspId) || textToSearch.trim().length < 2) {
      return;
    }

    // Prevent duplicate API calls for the same input
    const currentInput = `${textToSearch.trim()}_${customerId}_${mspId}`;
    if (lastApiCallRef.current === currentInput) {
      return;
    }

    lastApiCallRef.current = currentInput;
    
    // Convert customerId to string if it's a number, or use undefined if null
    const customerIdParam = customerId ? String(customerId) : undefined;
    const mspIdParam = mspId ? String(mspId) : undefined;
    const ticketIdParam = ticketId ? String(ticketId) : undefined;
    
    // Make API call using debounced input - this respects parent's debouncing
    dispatch(fetchSuggestions(textToSearch.trim(), customerIdParam, mspIdParam, ticketIdParam));
  }, [debouncedInputText, inputText, customerId, mspId, ticketId, dispatch, visible]); // debouncedInputText takes priority

  // Handle keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (selectedIndex === -1) {
            // First navigation - start at index 0
            setSelectedIndex(0);
          } else {
            setSelectedIndex((prev) => (prev + 1) % suggestions.length);
          }
          setHasNavigated(true); // User navigated to suggestions
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (selectedIndex === -1) {
            // First navigation - start at last index
            setSelectedIndex(suggestions.length - 1);
          } else {
            setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
          }
          setHasNavigated(true); // User navigated to suggestions
          break;
        case 'Enter':
          // Only handle Enter if user has navigated to suggestions and has a valid selection
          if (hasNavigated && selectedIndex >= 0 && suggestions[selectedIndex]) {
            e.preventDefault();
            e.stopPropagation(); // Prevent event from bubbling up to InputBox
            onSelect(suggestions[selectedIndex].text);
          }
          // If user hasn't navigated, let the parent component handle Enter (send typed text)
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, selectedIndex, suggestions, onSelect, onClose, hasNavigated]);

  // Only show typeahead when visible, has valid input, and has suggestions (not loading, not empty)
  if (!visible || !inputText.trim() || inputText.trim().length < 2 || isLoading || suggestions.length === 0) {
    return null;
  }

  // Debug logging to see suggestions structure

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        width: '100%',
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 30,
        mb: 1,
        maxHeight: '200px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #F3F4F6',
          background: '#F9FAFB',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#6B7280',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Quick suggestions
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 20,
            height: 20,
            color: '#9CA3AF',
            '&:hover': { color: '#6B7280' },
          }}
        >
          ×
        </IconButton>
      </Box>

      {/* Options */}
      <Box sx={{ maxHeight: '150px', overflowY: 'auto' }}>
        {suggestions.map((suggestion: any, index: number) => {
          // Defensive programming: ensure suggestion has the expected structure
          if (!suggestion || typeof suggestion !== 'object') {
            return null;
          }
          
          const suggestionText = suggestion.text || suggestion;
          const suggestionType = suggestion.type;
          
          return (
            <Box
              key={suggestion.id || index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1, // Reduced from 1.5 to 1 for less vertical padding
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                borderBottom: index < suggestions.length - 1 ? '1px solid #F3F4F6' : 'none',
                background: selectedIndex >= 0 && index === selectedIndex ? '#F3F4F6' : 'transparent',
                '&:hover': {
                  background: selectedIndex >= 0 && index === selectedIndex ? '#E5E7EB' : '#F9FAFB',
                },
                '&:active': {
                  background: '#E5E7EB',
                },
              }}
              onClick={() => onSelect(suggestionText)}
            >
              <SearchIcon
                sx={{
                  color: '#9CA3AF',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              />
              {/* Type Pill */}
              {suggestionType && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    backgroundColor: suggestionType === 'Action' ? '#DBEAFE' : 
                                   suggestionType === 'Docs' ? '#D1FAE5' : '#FEF3C7',
                    color: suggestionType === 'Action' ? '#1E40AF' : 
                           suggestionType === 'Docs' ? '#065F46' : '#92400E',
                    border: '1px solid',
                    borderColor: suggestionType === 'Action' ? '#BFDBFE' : 
                                suggestionType === 'Docs' ? '#A7F3D0' : '#FDE68A',
                    flexShrink: 0,
                  }}
                >
                  {suggestionType}
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: 400,
                  fontFamily: 'Inter, sans-serif',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '20px',
                  letterSpacing: '-0.01em',
                }}
              >
                {suggestionText}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Typeahead;
